import { useEffect, useState } from 'react'
import { unstable_batchedUpdates } from 'react-dom'

type Subscriber = () => void

const subscribers = new Set<Subscriber>()

export type IResponsiveType = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | string

type ResponsiveConfig = {
  [key in IResponsiveType]: number
}
export type ResponsiveInfo = {
  [key in IResponsiveType]: boolean
}

let info: ResponsiveInfo

// 1440分辨率以上屏幕为主
const defaultInfo: ResponsiveInfo = {
  sm: true,
  md: true,
  lg: true,
  xl: false,
  '2xl': false,
}

// 从小到大排序
export const responsiveConfig: ResponsiveConfig = {
  sm: 1024,
  md: 1280,
  lg: 1440,
  xl: 1680,
  '2xl': 1920,
}

export const responsiveTypes: IResponsiveType[] = [
  'sm',
  'md',
  'lg',
  'xl',
  '2xl',
]

let isResponsivePaused = false

// 开启/暂停 响应式下发
export function toggleResponsivePausedStatus(target?: boolean) {
  isResponsivePaused = target === undefined ? !isResponsivePaused : target
}

interface IMediaSqlHandler {
  key: IResponsiveType
  mediaSql: MediaQueryList
  handler: (event: MediaQueryListEvent) => void
}

let removeResponsiveListener: (() => void) | null
let curResponsiveType: IResponsiveType = 'lg'
let isUpdated = false

function addResponsiveListener() {
  if (removeResponsiveListener) {
    return
  }

  let mediaSqlHandlers: IMediaSqlHandler[] | null = Object.keys(
    responsiveConfig
  ).map((key) => {
    return {
      key,
      mediaSql: window.matchMedia(`(min-width: ${responsiveConfig[key]}px)`),
      handler: (event: MediaQueryListEvent) => {
        if (isResponsivePaused) {
          return
        }
        const newInfo = { ...info }
        newInfo[key] = event.matches
        info = newInfo
        isUpdated = true

        unstable_batchedUpdates(() => {
          subscribers.forEach((subscriber) => {
            subscriber()
          })
        })
      },
    }
  })

  const newInfo: ResponsiveInfo = {}

  mediaSqlHandlers!.forEach(({ key, mediaSql, handler }) => {
    newInfo[key] = mediaSql.matches
    if (mediaSql.addListener) {
      // 兼容处理
      mediaSql.addListener(handler)
    } else {
      mediaSql.addEventListener('change', handler)
    }
  })

  info = newInfo
  isUpdated = true

  removeResponsiveListener = () => {
    mediaSqlHandlers!.forEach(({ mediaSql, handler }) => {
      if (mediaSql.removeListener) {
        mediaSql.removeListener(handler)
      } else {
        mediaSql.removeEventListener('change', handler)
      }
    })
    mediaSqlHandlers = null
    removeResponsiveListener = null
  }
}

export function useResponsive(): ResponsiveInfo {
  const [state, setState] = useState<ResponsiveInfo>(defaultInfo)

  useEffect(() => {
    addResponsiveListener()
    setState(info)
    const subscriber = () => {
      setState(info)
    }
    subscribers.add(subscriber)
    return () => {
      subscribers.delete(subscriber)
      if (subscribers.size === 0) {
        if (removeResponsiveListener) {
          removeResponsiveListener()
        }
      }
    }
  }, [])

  return state
}

export function computedResponsive(): IResponsiveType {
  // 缓存计算结果
  if (isUpdated) {
    for (let i = responsiveTypes.length - 1; i >= 0; i--) {
      if (info[responsiveTypes[i]]) {
        curResponsiveType = responsiveTypes[i]
        break
      }
    }
    isUpdated = false
  }
  return curResponsiveType
}
